import { useTraduccion } from '@tallerya/i18n';
import { useEffect, useRef, type ReactNode } from 'react';
import { Ayuda } from '../Ayuda';
import { Boton } from '../Boton';
import { IconoAlerta, IconoCheck } from '../iconos';
import type { AvisoFormulario, EstadoFormulario } from './useFormulario';
import './formulario.css';

// El patrón de formulario (F-8), la parte visible. Sale de «Productos/Servicios -
// Agregar servicio» del Figma y de sus variantes (Errores), (Guardando) y (Error
// al guardar). De arriba abajo:
//
//   Título ⓘ
//   Descripción (o la línea «Creado el… · Última modificación…» al editar)
//   [Aviso rojo, si algo salió mal]
//   ── Sección ─────────────────────────────────
//   Campos…
//   ── ──────────────────────────────────────────
//   [Guardar] [Guardar y agregar otro] [Cancelar]            [Desactivar]

interface Props<V> {
  form: EstadoFormulario<V>;
  titulo: string;
  /** Datos que se leen siempre, como el código al editar. La explicación va en `info`. */
  descripcion?: ReactNode;
  /**
   * Qué es la pantalla, en el globo del ⓘ junto al título (#120). **Obligatorio**:
   * toda pantalla explica para qué sirve, y que lo pida el tipo es lo que hace
   * que el ⓘ salga siempre y no solo donde alguien se acordó.
   */
  info: string;
  children: ReactNode;

  /** «Guardar» al crear, «Guardar cambios» al editar. */
  etiquetaGuardar?: string;
  /** Muestra «Guardar y agregar otro». Tiene sentido en las altas, no en la edición. */
  guardarYOtro?: boolean;
  /**
   * Qué hacer después de guardar bien. `otro` viene del botón «Guardar y agregar
   * otro»: la pantalla suele reiniciar el formulario y quedarse.
   */
  onGuardado: (siguiente: 'volver' | 'otro') => void;
  onCancelar: () => void;
  /** Lo que va solo a la derecha de la barra: el «Desactivar» de la edición. */
  accionSecundaria?: ReactNode;
}

export function Formulario<V>({
  form,
  titulo,
  descripcion,
  info,
  children,
  etiquetaGuardar,
  guardarYOtro = false,
  onGuardado,
  onCancelar,
  accionSecundaria,
}: Props<V>) {
  const { t } = useTraduccion();
  const raiz = useRef<HTMLFormElement>(null);

  async function enviarY(siguiente: 'volver' | 'otro') {
    if (await form.enviar()) onGuardado(siguiente);
  }

  // Un aviso sin campos en rojo —otra persona guardó antes, no hubo red— sale
  // arriba, y «Guardar» está abajo: sin esto, la pantalla se quedaba donde estaba
  // y no se veía que no se guardó (#178). Se lleva a la vista y recibe el foco,
  // que es lo que el lector de pantalla necesita para seguir desde ahí. Con campos
  // en rojo no: el foco ya va al primero, y moverlo acá lo desharía.
  //
  // Una sola vez por aviso: corregir un campo cambia los errores, y el aviso que
  // sigue ahí no tiene que volver a llevarse la pantalla.
  const avisoLlevado = useRef<typeof form.aviso>(null);
  const conCamposEnRojo = Object.keys(form.errores).length > 0;
  useEffect(() => {
    if (!form.aviso || form.aviso === avisoLlevado.current) return;
    avisoLlevado.current = form.aviso;
    if (conCamposEnRojo) return;
    const aviso = raiz.current?.querySelector<HTMLElement>('.ui-formulario__aviso');
    aviso?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    aviso?.focus({ preventScroll: true });
  }, [form.aviso, conCamposEnRojo]);

  return (
    // `noValidate`: la validación la hace el formulario con los mismos códigos que
    // la API. Los globos del navegador saldrían en el idioma del sistema, no en
    // el que eligió la persona.
    <form
      ref={raiz}
      className="ui-formulario"
      noValidate
      aria-busy={form.guardando}
      onSubmit={(e) => {
        e.preventDefault();
        void enviarY('volver');
      }}
    >
      <header className="ui-formulario__encabezado">
        <div className="ui-formulario__titulo">
          <h1>{titulo}</h1>
          <Ayuda tema={titulo} texto={info} tono="titulo" />
        </div>
        {descripcion && <p>{descripcion}</p>}
      </header>

      {form.aviso && <AvisoError aviso={form.aviso} />}

      {children}

      <div className="ui-formulario__acciones">
        <div className="ui-formulario__acciones-principales">
          <Boton
            type="submit"
            icono={form.guardando ? undefined : <IconoCheck />}
            disabled={form.guardando}
            aria-busy={form.guardando}
            className="ui-formulario__guardar"
          >
            {form.guardando ? t('ui.formulario.guardando') : (etiquetaGuardar ?? t('app.guardar'))}
          </Boton>
          {guardarYOtro && (
            <Boton variante="secundario" disabled={form.guardando} onClick={() => void enviarY('otro')}>
              {t('ui.formulario.guardarYOtro')}
            </Boton>
          )}
          <Boton variante="texto" disabled={form.guardando} onClick={onCancelar}>
            {t('app.cancelar')}
          </Boton>
        </div>
        {accionSecundaria}
      </div>
    </form>
  );
}

/**
 * El aviso rojo de arriba. `role="alert"` hace que el lector de pantalla lo lea
 * apenas aparece, sin que la persona tenga que ir a buscarlo. `tabIndex={-1}`
 * para que `Formulario` le pueda pasar el foco sin que entre en el orden del Tab.
 *
 * La `accion` va como botón secundario a la derecha («Recargar», #178), como en el
 * Figma «Clientes - Editar cliente (Cambio simultáneo)».
 */
export function AvisoError({ aviso }: { aviso: AvisoFormulario }) {
  return (
    <div
      className={`ui-formulario__aviso${aviso.accion ? ' ui-formulario__aviso--con-accion' : ''}`}
      role="alert"
      tabIndex={-1}
    >
      <IconoAlerta />
      <div>
        <strong>{aviso.titulo}</strong>
        <p>{aviso.texto}</p>
        {aviso.enlace && (
          <button type="button" className="ui-formulario__aviso-enlace" onClick={aviso.enlace.alHacerClic}>
            {aviso.enlace.texto}
          </button>
        )}
      </div>
      {aviso.accion && (
        <Boton variante="secundario" onClick={aviso.accion.alHacerClic}>
          {aviso.accion.texto}
        </Boton>
      )}
    </div>
  );
}

/** Un bloque del formulario, con su divisor arriba: «Información general», «Precio y tiempo». */
export function Seccion({
  titulo,
  info,
  children,
}: {
  titulo: string;
  /** Qué es la sección, en el globo del ⓘ junto al título (#120). */
  info?: string;
  children: ReactNode;
}) {
  return (
    <section className="ui-formulario__seccion">
      <div className="ui-formulario__seccion-titulo">
        <div className="ui-formulario__titulo">
          <h2>{titulo}</h2>
          {info && <Ayuda tema={titulo} texto={info} tono="titulo" />}
        </div>
        
      </div>
      {children}
    </section>
  );
}

/** Campos lado a lado. En pantallas angostas bajan uno debajo del otro. */
export function Fila({ children }: { children: ReactNode }) {
  return <div className="ui-formulario__fila">{children}</div>;
}
